import './HighlightBanner.css';
import React from 'react';
import { useSitecoreContext } from '../../utils/SitecoreContextReactContext';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { ReactComponent as ChevronRight } from '../../resources/icons/IconChevronRightLight.svg';
import { ReactComponent as ChevronLeft } from '../../resources/icons/IconChevronLeftLight.svg';
import Spoiler from '../atoms/Spoiler/Spoiler';
import { get, getGlobalDictionaryTerm } from '../../utils';
import { convertToAnalyticObject } from '../../utils/analytics';
import { isNil, isNotNil } from '../../utils/IsNil';
import ComponentsWrapper from '../atoms/ComponentsWrapper';
import { hexToRgb } from '../../utils/hexToRgb';
import { tealiumClickFunc, tealiumMergeClickData } from '../../utils/tealium';
import { ReactComponent as ChevronDetLeftIcon } from '../../resources/icons/chevron-left-16-px.svg';
import { ReactComponent as ChevronDetRightIcon } from '../../resources/icons/chevron-right-16-px.svg';
const HighlightBanner = _ref => {
  let {
    fields,
    params
  } = _ref;
  const sitecoreContext = useSitecoreContext();
  const newWindowLabel = getGlobalDictionaryTerm(sitecoreContext, 'newWindowLabel') || "Opens in a new window";
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const isDetSite = get(params, 'RenderingStyle') === 'DDA' || false;
  const reverseMedia = params !== undefined && params['Left Media'] === '1' ? true : false;
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const primaryPageThemeColor = get(sitecoreContext, 'themesOfPage.primary') || "#035E88";
  const variables = {
    '--highlight-banner-background-with-opacity': !isDetSite ? hexToRgb(primaryPageThemeColor).concat(", 0.1") : hexToRgb("#FBF8FD").concat(", 1") //DDA hardcoded color
  };
  const banner = get(fields, "data.item", []);
  const bannerTitle = get(banner, "title.jss.value");
  const bannerDescription = get(banner, "description.jss.value");
  const bannerCtaLink = get(banner, "ctaLink.jss.value.href");
  const bannerCtaTarget = get(banner, "ctaLink.jss.value.target");
  const bannerCtaLabelValue = get(banner, "ctaLabel.jss.value");
  const bannerImageSrc = get(banner, "image.jss.value.src");
  const bannerImageAlt = get(banner, "image.jss.value.alt");
  if (isNil(banner) || isNil(bannerTitle) || isNil(bannerDescription)) {
    console.warn('HighlightBanner component render skipped. No fields provided');
    if (pageEditing) {
      return /*#__PURE__*/React.createElement("div", {
        className: "no-data"
      }, " [HighlightBanner component is empty: please provide data source]");
    }
    return null;
  }
  const accordion = get(banner, "accordion", []).filter(el => el.title && el.title.jss.value && el.description && el.description.jss.value);
  const renderAccordion = accordion => {
    if (Array.isArray(accordion) && !accordion.length) {
      return null;
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "accordion__spoilers-wrapper"
    }, accordion.map((el, i) => {
      const accordionTitle = el.title.jss.value;
      const accordionDescriptionJss = el.description.jss;
      const accordionCtaLinkHref = el.ctaLink && el.ctaLink.jss.value.href;
      const accordionCtaLinkTarget = el.ctaLink && el.ctaLink.jss.value.target;
      const accordionCtaLabelValue = el.ctaLabel && el.ctaLabel.jss.value;
      const isTargetBlank = accordionCtaLinkTarget === '_blank';
      const icon = get(el, 'iconSvgCode.jss.value');
      return /*#__PURE__*/React.createElement(Spoiler, {
        onToggle: val => tealiumClickFunc(tealiumMergeClickData(eventsAnalytics["accordionAction"], params), [accordionTitle, val ? "expand" : "collapse"]),
        key: i,
        title: /*#__PURE__*/React.createElement(React.Fragment, null, icon ? /*#__PURE__*/React.createElement("div", {
          dangerouslySetInnerHTML: {
            __html: icon
          },
          className: "spoiler__title-icon"
        }) : /*#__PURE__*/React.createElement(React.Fragment, null), /*#__PURE__*/React.createElement("span", null, accordionTitle)),
        isDetSite: isDetSite
      }, /*#__PURE__*/React.createElement(RichText, {
        tag: "div",
        field: accordionDescriptionJss
      }), isNotNil(el.ctaLink) && el.ctaLink.jss.value.href && isNotNil(el.ctaLabel) && el.ctaLabel.jss.value && /*#__PURE__*/React.createElement("a", {
        onClick: () => tealiumClickFunc(tealiumMergeClickData(eventsAnalytics["ctaClick"], params), [accordionTitle, el.ctaLabel ? el.ctaLabel.jss.value : ""]),
        className: "accordion__cta",
        href: accordionCtaLinkHref,
        target: accordionCtaLinkTarget,
        "aria-label": isTargetBlank ? `${accordionCtaLabelValue} | ${newWindowLabel}` : bannerCtaLabelValue,
        rel: isTargetBlank ? "noreferrer noopener" : ''
      }, isNotNil(el.ctaLabel) ? accordionCtaLabelValue : null, rtl ? /*#__PURE__*/React.createElement(ChevronLeft, {
        className: "btn-link__icon"
      }) : /*#__PURE__*/React.createElement(ChevronRight, {
        className: "btn-link__icon"
      })));
    }));
  };
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl,
    style: variables,
    anchorUrlParameter: params['Anchor Url Parameter'],
    isDetSite: isDetSite
  }, /*#__PURE__*/React.createElement("div", {
    className: `accordion accordion__content ${bannerImageSrc && reverseMedia ? 'accordion__content--reverse-media' : ''}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "accordion__text-content"
  }, bannerTitle && /*#__PURE__*/React.createElement("h1", {
    className: "accordion__title"
  }, bannerTitle), /*#__PURE__*/React.createElement(RichText, {
    tag: "div",
    className: "accordion__description",
    field: banner.description.jss
  }), banner.ctaLink && bannerCtaLink && bannerCtaLabelValue && /*#__PURE__*/React.createElement("a", {
    className: "accordion__cta",
    href: bannerCtaLink,
    target: bannerCtaTarget,
    "aria-label": bannerCtaTarget === '_blank' ? `${bannerCtaLabelValue} | ${newWindowLabel}` : bannerCtaLabelValue
  }, banner.ctaLabel && bannerCtaLabelValue, isDetSite ? rtl ? /*#__PURE__*/React.createElement(ChevronDetLeftIcon, {
    className: "btn-link__icon"
  }) : /*#__PURE__*/React.createElement(ChevronDetRightIcon, {
    className: "btn-link__icon"
  }) : rtl ? /*#__PURE__*/React.createElement(ChevronLeft, {
    className: "btn-link__icon"
  }) : /*#__PURE__*/React.createElement(ChevronRight, {
    className: "btn-link__icon"
  })), renderAccordion(accordion)), isNotNil(banner.image) && bannerImageSrc && /*#__PURE__*/React.createElement("img", {
    className: "accordion__image",
    src: bannerImageSrc,
    alt: bannerImageAlt
  })));
};
export default HighlightBanner;
const StyledWrapper = _ref2 => {
  let {
    children,
    rtl,
    style,
    anchorUrlParameter,
    isDetSite
  } = _ref2;
  const commonStyles = isDetSite ? "cyl0sx8" : "c1uo9fef";
  const rtlStyles = isDetSite ? "rvgzgeq" : "r1v4jx27";
  const ltrStyles = isDetSite ? "l1x9gah3" : "ld9ve17";
  return /*#__PURE__*/React.createElement(ComponentsWrapper, {
    style: style,
    className: `${commonStyles} ${rtl ? rtlStyles : ltrStyles}`,
    anchorUrlParameter: anchorUrlParameter
  }, children);
};